/* EcommerceTab — Order Detail Row (expanded view) */
import React from 'react';
import { Truck, CheckCircle2, FileCheck, Loader2 } from 'lucide-react';

const fmt = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDt = (o) => {
    if (o?.order_datetime) return o.order_datetime.slice(0, 16).replace('T', ' ');
    return o?.order_date || '';
};

const EVENT_LABEL = {
    PRE_ANNOUNCED: { tr: 'Bildirildi', tone: 'gray' },
    INBOUND_COLLECT: { tr: 'Alındı', tone: 'sky' },
    AT_TRANSPORTER: { tr: 'Kargoda', tone: 'sky' },
    IN_TRANSIT: { tr: 'Yolda', tone: 'sky' },
    OUT_FOR_DELIVERY: { tr: 'Dağıtımda', tone: 'amber' },
    DELIVERY_ATTEMPT: { tr: 'Teslim Denendi', tone: 'amber' },
    DELIVERED: { tr: 'Teslim Edildi', tone: 'emerald' },
    DELIVERED_AT_CUSTOMER: { tr: 'Teslim Edildi', tone: 'emerald' },
    DELIVERED_AT_NEIGHBOURS: { tr: 'Komşuya Teslim', tone: 'emerald' },
    DELIVERED_AT_PICKUP_POINT: { tr: 'Pakomata', tone: 'emerald' },
    DELIVERED_AT_SERVICE_POINT: { tr: 'Şubeye Teslim', tone: 'emerald' },
    RETURNED_TO_SENDER: { tr: 'Geri Döndürüldü', tone: 'rose' },
    CANCELLED: { tr: 'İptal Edildi', tone: 'rose' },
};
const TONE_CLS = {
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    sky: 'bg-sky-100 text-sky-800 border-sky-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    rose: 'bg-rose-100 text-rose-800 border-rose-200',
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
};

function TrackBadge({ tracking, listStatus, testId }) {
    // Iter 113.8: latest_event yoksa list_status veya track_and_trace bilgisinden Gönderildi göster
    const t = tracking || {};
    if (t.latest_event) {
        const meta = EVENT_LABEL[t.latest_event] || { tr: t.latest_event, tone: 'gray' };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold border ${TONE_CLS[meta.tone]}`}
                data-testid={testId}>
                <Truck size={10} /> {meta.tr}
            </span>
        );
    }
    // Fallback: list_status veya track no varsa "Gönderildi" rozeti
    const isShipped = listStatus === 'shipped' || t.track_and_trace || t.code;
    if (isShipped) {
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold border ${TONE_CLS.sky}`}
                data-testid={testId}>
                <Truck size={10} /> Gönderildi
            </span>
        );
    }
    if (listStatus === 'cancelled') {
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold border ${TONE_CLS.rose}`}
                data-testid={testId}>
                İptal
            </span>
        );
    }
    return <span className="text-[11px] text-gray-400" data-testid={testId}>Beklemede</span>;
}

export default function OrderDetailRow({ order, name, cust, pushed, pushingId, onPush }) {
    const o = order;
    return (
        <tr className="bg-gray-50/50">
            <td colSpan={7} className="p-0">
                <div className="p-4 border-t-2 border-emerald-200" data-testid={`bol-detail-${o.order_id}`}>
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3 pb-3 border-b border-gray-200">
                        <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Bol.com Siparis Detayi</div>
                            <div className="text-base font-bold text-gray-900 mt-0.5">#{o.order_id}</div>
                            <div className="text-xs text-gray-600 mt-1">{fmtDt(o)} · {o.raw_status || 'OPEN'}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs text-gray-500">Toplam (BTW dahil)</div>
                            <div className="text-lg font-bold text-gray-900">{fmt(o.total_incl_vat)} €</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        {/* Musteri */}
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Musteri</div>
                            <div className="text-sm font-medium text-gray-900">{name}</div>
                            {cust.email && <div className="text-xs text-gray-600 mt-0.5">{cust.email}</div>}
                            {cust.vat_number && <div className="text-xs text-gray-600 mt-0.5">VAT: {cust.vat_number}</div>}
                            {cust.address && (cust.address.street || cust.address.city) && (
                                <div className="text-xs text-gray-600 mt-1.5 pt-1.5 border-t border-gray-100">
                                    {cust.address.street} {cust.address.house}{cust.address.extension ? ` ${cust.address.extension}` : ''}<br />
                                    {cust.address.zip} {cust.address.city} {cust.address.country}
                                </div>
                            )}
                        </div>
                        {/* Kargo + Billit durum */}
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2 flex items-center gap-1">
                                <Truck size={11} /> Kargo & Fatura
                            </div>
                            {o.tracking?.latest_event ? (
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <TrackBadge tracking={o.tracking} />
                                        {o.tracking.latest_event_at && (
                                            <span className="text-[10px] text-gray-500">
                                                {o.tracking.latest_event_at.slice(0, 16).replace('T', ' ')}
                                            </span>
                                        )}
                                    </div>
                                    {o.tracking.transporter_code && (
                                        <div className="text-xs text-gray-600">
                                            Kargo: <b>{o.tracking.transporter_code}</b>
                                        </div>
                                    )}
                                    {o.tracking.track_and_trace && (
                                        <div className="text-xs text-gray-600">
                                            Takip: <span className="font-mono">{o.tracking.track_and_trace}</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-xs text-gray-500">Kargo bilgisi yok</div>
                            )}
                            <div className="mt-2 pt-2 border-t border-gray-100">
                                {o.billit_pushed ? (
                                    <div className="text-xs text-emerald-700 flex items-center gap-1">
                                        <CheckCircle2 size={11} /> Billit fatura: #{o.billit_order_id}
                                    </div>
                                ) : (
                                    <div className="text-xs text-gray-500">Billit'e gonderilmedi</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Urun satirlari */}
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="px-3 py-2 bg-gray-50 border-b text-xs uppercase tracking-wide font-semibold text-gray-600">
                            Urunler ({(o.lines || []).length})
                        </div>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs text-gray-500 border-b">
                                    <th className="text-left p-2 font-medium">Urun</th>
                                    <th className="text-left p-2 font-medium">EAN</th>
                                    <th className="text-right p-2 font-medium">Adet</th>
                                    <th className="text-right p-2 font-medium">Birim (BTW dahil)</th>
                                    <th className="text-right p-2 font-medium">Toplam</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {(o.lines || []).map((ln, i) => (
                                    <tr key={i}>
                                        <td className="p-2 text-gray-900">{ln.title}</td>
                                        <td className="p-2 font-mono text-xs text-gray-600">{ln.ean}</td>
                                        <td className="p-2 text-right">{ln.quantity}</td>
                                        <td className="p-2 text-right">{fmt(ln.unit_price_incl_vat)} €</td>
                                        <td className="p-2 text-right font-semibold">{fmt(ln.line_total_incl_vat)} €</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Bottom actions */}
                    {!pushed && (
                        <div className="mt-3 flex justify-end">
                            <button
                                onClick={() => onPush(o)}
                                disabled={pushingId === o.order_id}
                                className="h-10 px-5 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg flex items-center gap-2 shadow-sm"
                                data-testid={`bol-detail-push-${o.order_id}`}>
                                {pushingId === o.order_id
                                    ? <Loader2 size={14} className="animate-spin" />
                                    : <FileCheck size={14} />}
                                Billit'e Aktar (Otomatik Fatura)
                            </button>
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
}
